<template>
    <div class="flex flex-column grow play-alongs tw-pt-[20px]">
        <CollectionFilterWrapper v-if="showFilters" active-tab="All Play-Alongs" :multi-select-columns="filterOptions"
            :search-term="searchTerm" :selected-filters="selectedFilters" :selected-sort="sort"
            :selected-progress="progress" :sort-options="sortOptions" :tab-options="tabOptions"
            @on-filter-change="handleFilterChange" @on-sort-change="handleContentSort"
            @on-search-change="handleTriggerSearch" @on-clear-filter="handleClearFilter"
            @on-progress-change="handleProgressChange">
            <template #extra-icon-left>
                <button
                    class="tw-flex tw-items-center tw-justify-center tw-ml-[12px] tw-shrink-0 tw-w-[45px] tw-h-[45px] tw-border tw-border-[#CBCBCD] dark:tw-border-white tw-rounded-full"
                    :class="isShuffle ? 'tw-border-[#000C17] tw-bg-[#000C17] dark:tw-bg-white tw-text-white dark:tw-text-[#000C17]' : 'hover:tw-border-[#000C17] hover:tw-bg-[#000C17] hover:dark:tw-bg-white hover:tw-text-white hover:dark:tw-text-[#000C17] tw-text-[#000C17] dark:tw-text-white tw-bg-white dark:tw-bg-transparent'"
                    title="Toggle Shuffle" @click="toggleShuffle">
                    <musora-icon icon-name="random" class="tw-w-5 tw-h-5" />
                </button>
            </template>
        </CollectionFilterWrapper>
        <div class="tw-flex tw-flex-col tw-grow">
            <SkeletonListCatalogueItem v-if="isPageLoading" v-for="i in 8" :key="i" />

            <PlayAlongsListItem v-else-if="content.length > 0" v-for="(item, i) in content" :ref="`list${item.id}`"
                :key="`list${item.id}`" :index="i + 1" :item="item" :brand="brand"
                :active="activeItem != null ? item.id === activeItem.id : false" :display-user-interactions="false"
                :no-link="true" :theme-color="themeColor" :show-user-actions="showUserActions"
                @addToList="addToListEventHandler" @markAsComplete="completedEventHandler"
                @update-track="updateTrack" />

            <div v-else-if="content.length === 0" class="flex flex-column dark:tw-text-white">
                <p class="body mb-2">
                    We're sorry, no results were found using those filters.
                    Try removing a filter to broaden your search.
                </p>
                <p class="body">
                    If you're certain you should be seeing content,
                    please contact support by using the chat widget on the bottom right of your screen.
                    Alternatively, you can email <a href="mailto:support@musora.com">support@musora.com</a>.
                </p>
            </div>
        </div>
        <div v-if="showPagination" class="flex flex-row pagination-row align-h-right">
            <pagination :current-page="Number(page)" :total-pages="totalPages" @pageChange="handlePageChange">
            </pagination>
        </div>
        <transition name="show-from-bottom">
            <div v-show="loading" class="loading corners pa-2" @click.stop.prevent>
                <LoadingAnimation :theme-color="themeColor" />
            </div>
        </transition>

        <PlayAlongsPlayer v-show="activeItem != null" :no-sidebar="noSidebar" ref="progressBar"
            :active-item="activeItem" :audio-player="audioPlayer" :drums="drums" :metronome="metronome" :loop="loop"
            :current-time="currentTime" :total-duration="totalDuration" :current-position="currentPosition"
            :is-playing="isPlaying" :anchor-offsets="anchorOffsets" :current-mouse-x="currentMousePosition.x"
            :played-content="playedContent" :total-results="totalResults" :is-shuffle="isShuffle"
            :current-volume="Number(currentVolume * 100)" @playPause="playPause" @nextTrack="playNextTrack"
            @previousTrack="playPreviousTrack" @seek="seek" @drums="toggleDrums" @metronome="toggleMetronome"
            @loop="toggleLoop" @anchorMouseDown="handleAnchorMouseDown" @anchorButtonClick="handleAnchorButtonClick"
            @volumeChange="changeVolume" />
        <div class="flex flex-row hide">
            <audio id="playAlongsAudioPlayer" ref="audioPlayer" preload="auto"></audio>
        </div>
    </div>
</template>
<script>
import * as QueryString from 'query-string';
import Utils from '../../assets/js/helper-functions/utils.js';
import ContentService from '../../assets/js/Services/content';
import userJourney from '@services/userJourney';
import PlayAlongsListItem from './PlayAlongsListItem.vue';
import UserCatalogueEvents from '../../mixins/UserCatalogueEvents';
import PlayAlongsPlayer from './PlayAlongsPlayer.vue';
import ContentModel from '../../assets/js/models/_model';
import EventHandlers from './event-handlers';
import ThemeClasses from '../../mixins/ThemeClasses';
import PlayAlongsFilters from './PlayAlongsFilters.vue';
import Pagination from '../../Components/Pagination.vue';
import LoadingAnimation from '../../Components/LoadingAnimation/LoadingAnimation.vue';
import CollectionFilterWrapper from '@collections/Filter/CollectionFilterWrapper';
import ProgressTracker from "../../assets/js/classes/progress-tracker";
import InputLabel from "@units/InputLabel/InputLabel.vue";
import { bgColor, textColor } from "@constants/brands";
import { useFilterValues } from "@hooks/useFilterValues";
import { useUserStore } from '@stores/user.js';
import { fetchAll, fetchPlayAlongsCount, contentStatusCompleted, contentStatusReset, fetchByRailContentIds, fetchAllFilterOptions } from 'musora-content-services';
import { usePlatformStore } from "@stores/platform";
import { storeToRefs } from "pinia/dist/pinia";
import SkeletonListCatalogueItem from '@collections/SkeletonLoader/SkeletonListCatalogueItem';

const { getFilterValues } = useFilterValues();

export default {
    name: 'PlayAlongs',
    components: {
        PlayAlongsListItem,
        PlayAlongsPlayer,
        PlayAlongsFilters,
        LoadingAnimation,
        Pagination,
        InputLabel,
        CollectionFilterWrapper,
        SkeletonListCatalogueItem,
    },
    mixins: [ThemeClasses, UserCatalogueEvents, EventHandlers],
    inject: {
        isSidebarCollapsed: {
            from: 'isSidebarCollapsed'
        }
    },
    props: {
        brand: {
            type: String,
            default: () => 'drumeo',
        },
        noSidebar: {
            type: Boolean,
            default: () => false,
        },
        idsToPull: {
            type: Object,
            default: () => null,
        },
        userPlaylistId: {
            type: [String, Number],
            default: () => null,
        },
        contentEndpoint: {
            type: String,
            default: () => '/railcontent/content',
        },
        showFilters: {
            type: Boolean,
            default: () => true,
        },
        showPagination: {
            type: Boolean,
            default: () => true,
        },
        useUrlParams: {
            type: Boolean,
            default: () => true,
        },
        showUserActions: {
            type: Boolean,
            default: () => true,
        },
        sessionToken: {
            type: String,
            default: () => null,
        },
        trackProgress: {
            type: Boolean,
            default: () => true,
        },
    },
    data() {
        return {
            searchTerm: null,
            content: [],
            loading: false,
            currentPlaybackRate: 1,
            currentVolume: 1,
            page: 1,
            limit: 20,
            sort: '-published_on',
            progress: '',
            totalResults: 0,
            filterOptions: [],
            isKeyboardControlsEnabled: false,
            selectedFilters: [],
            shufflePlaylist: [],
            playedContent: [],
            showFavoritesOnly: false,
            showCompletedOnly: false,
            isShuffle: false,
            activeItem: null,
            audioPlayer: this.$refs.audioPlayer,
            drums: false,
            metronome: true,
            loop: false,
            currentTime: 0,
            totalDuration: 0,
            anchorOffsets: {
                a: 0,
                b: 100,
            },
            anchorMouseDown: {
                a: false,
                b: false,
            },
            currentMousePosition: {
                x: 0,
                y: 0,
            },
            showMobileFilters: false,
            displayFilters: true,
            progressTracker: this.trackProgress ? new ProgressTracker() : null,
            isPlaying: false,
        };
    },
    computed: {
        brandTextColor() {
            return textColor[this.brand];
        },
        brandBgColor() {
            return bgColor[this.brand];
        },
        currentPosition() {
            return (this.currentTime / this.totalDuration) * 100;
        },
        filterQueryObject() {
            return {
                included_fields: this.selectedFilters,
                page: this.page,
                limit: this.limit,
                only_from_my_list: this.showFavoritesOnly,
                included_user_states: this.progress ? [this.progress] : undefined,
                sort: this.sort,
            };
        },
        filterQueryString() {
            return QueryString.stringify(this.filterQueryObject, { arrayFormat: 'bracket' });
        },
        sortOptions() {
            return [
                { value: '-published_on', name: 'Newest First', icon: 'sort-down', },
                { value: 'published_on', name: 'Oldest First', icon: 'sort-up', },
                { value: 'slug', name: 'Name: A to Z', icon: 'sort-name-asc', },
                { value: '-slug', name: 'Name: Z to A', icon: 'sort-name-desc', },
            ]
        },
        totalPages() {
            return Math.ceil(this.totalResults / this.limit) || 1;
        },
        tabOptions() {
            return [
                { key: `allPlayAlongs`, value: `All Play-Alongs` },
            ];
        },
        isPageLoading() {
            const platformStore = usePlatformStore();
            const { isLoading } = storeToRefs(platformStore);

            return isLoading.value;
        },
    },
    async beforeMount() {
        const platformStore = usePlatformStore();

        if (this.idsToPull) {
            await this.getContentByIds(this.idsToPull);
        } else {
            await this.getContent(false, false);
        }
        platformStore.setLoadingState(false);
    },
    mounted() {
        this.audioPlayer = this.$refs.audioPlayer;
        Object.keys(this.eventHandlers).forEach((event) => {
            this.audioPlayer.addEventListener(
                event,
                this.eventHandlers[event],
            );
        });
        if (this.useUrlParams) {
            this.getActiveFilters();
        }
        this.getDefaultVolume();
        this.addMouseEventHandlers();
        this.enableKeyboardControls();
        // Pause the player if any other audio players on the page are played
        const audioPlayers = document.querySelectorAll('audio');
        Array.from(audioPlayers).forEach((player) => {
            if (!player.matches('#playAlongsAudioPlayer')) {
                player.addEventListener('play', () => {
                    this.audioPlayer.pause();
                });
            }
        });
    },
    beforeDestroy() {
        Object.keys(this.eventHandlers).forEach((event) => {
            this.audioPlayer.removeEventListener(
                event,
                this.eventHandlers[event],
            );
        });
        this.removeMouseEventHandlers();
    },
    methods: {
        trackFilters() {
            const userStore = useUserStore();
            const payload = {
                brand: userStore.brand,
                section: userStore.journeySection,
                progress: this.filterOptions.included_user_states,
                filters: this.filterQueryObject.included_fields,
            };

            userJourney.trackFilter({
                token: userStore.token,
                payload
            });
        },
        trackSort() {
            const userStore = useUserStore();
            const payload = {
                sort: this.filterQueryObject.sort,
                section: userStore.journeySection,
                brand: userStore.brand,
            };

            userJourney.trackSort({
                token: userStore.token,
                payload
            })
        },

        async getContentByIds(ids) {
            const data = await fetchByRailContentIds(ids, 'play-along');
            this.content = data.map(item => ({
                ...item,
                need_access: false,
            }));

            this.$nextTick(() => {
                this.loading = false;
            });
        },

        async getContent(resetPlaylist, showLoading = true) {
            if (showLoading) this.loading = true;
            let progress = 'all';
            switch(this.progress){
                case 'started': progress = 'in progress';
                    break;
                case 'not-started': progress = 'not started';
                    break;
                case 'completed': progress = 'completed'
                    break;
            }
            const data = await fetchAll(this.brand, 'play-along', {
                page: this.page,
                limit: this.limit,
                searchTerm: this.searchTerm,
                sort: this.sort,
                includedFields: this.selectedFilters,
                groupBy: "",
                progressIds: undefined,
                useDefaultFields: true,
                customFields: [],
                progress: progress
            })

            //Count Patch
            fetchPlayAlongsCount(this.brand, {
                searchTerm: this.searchTerm,
                includedFields: this.selectedFilters,
                progressIds: undefined,
                progress: progress
            }).then(count => {
                this.totalResults = count;
            }).catch( error=> console.log('error fetching playalong count', error ) )

            this.content = data.entity;

            //Get Genre
            const genreField = this.selectedFilters.find(field => field.startsWith('genre'));
            const genre = genreField ? genreField.split(',')[1] : null;

            //Get Filter Options
            const filterOptions = await fetchAllFilterOptions(
                this.brand, //brand
                [ ...this.selectedFilters ], //filters array
                genre, //style
                "", //artist
                'play-along', //contentType
                this.searchTerm, //term
                undefined, //progressIds
                undefined, //coachIds
            );

            this.filterOptions = getFilterValues(filterOptions.meta.filterOptions);

            this.$nextTick(() => {
                this.loading = false;
                if (resetPlaylist) {
                    this.generateRandomPlaylist();
                }
            });
        },
        updateContent(resetPlaylist = false) {
            this.trackFilters();
            this.updatePageUrl();

            this.$nextTick(() => {
                this.playedContent = [];
                return this.getContent(resetPlaylist);
            });
        },
        parseBpmOptions(options) {
            const acceptedBpmOptions = [
                {
                    label: '50-90', min: 50, max: 90, active: false,
                },
                {
                    label: '91-120', min: 91, max: 120, active: false,
                },
                {
                    label: '121-150', min: 121, max: 150, active: false,
                },
                {
                    label: '151-180', min: 151, max: 180, active: false,
                },
                {
                    label: '181+', min: 181, max: 10000, active: false,
                },
            ];
            const parsedOptions = {
                difficulty: options.difficulty ? options.difficulty.sort((a, b) => a - b) : [],
                style: options.style,
                bpm: [],
            };
            if (options.bpm) {
                acceptedBpmOptions.forEach((option) => {
                    options.bpm.forEach((bpm) => {
                        if (bpm >= option.min && bpm <= option.max) {
                            // eslint-disable-next-line no-param-reassign
                            option.active = true;
                        }
                    });
                });
                parsedOptions.bpm = acceptedBpmOptions
                    .filter(option => option.active)
                    .map(option => option.label);
            }
            return parsedOptions;
        },
        getActiveFilters() {
            const urlParams = QueryString.parse(window.location.search, { arrayFormat: 'bracket' });
            this.page = urlParams.page || 1;
            this.limit = urlParams.limit || 20;
            this.sort = urlParams.sort || '-published_on';

            if (urlParams.included_fields != null && urlParams.included_fields.length > 0) {
                this.selectedFilters = urlParams.included_fields;
            }
            if (urlParams.only_from_my_list != null
                && urlParams.only_from_my_list == 1) {
                this.showFavoritesOnly = true;
            }
            if (urlParams.included_user_states != null
                && urlParams.included_user_states.length > 0) {
                this.progress = urlParams.included_user_states[0];
            }
        },
        updateTrack(item) {
            if (!item.need_access) {
                if (this.activeItem && item.id === this.activeItem.id) {
                    this.playPause();
                } else {
                    if (this.progressTracker) {
                        this.progressTracker.stop();
                        this.progressTracker.reset();
                    }
                    this.playTrack(item);
                    if (this.loop) {
                        this.resetAnchors();
                    }
                }
            }
        },
        playPause() {
            if (this.audioPlayer.paused === false) {
                this.isPlaying = false;
                this.audioPlayer.pause();
                this.sendProgressTracking();
            } else {
                this.isPlaying = true;
                this.audioPlayer.play();
            }
            this.$nextTick(() => this.$forceUpdate());
        },
        playTrack(item) {
            this.activeItem = new ContentModel('play-along', {
                brand: this.brand,
                post: item,
            });

            this.switchTrack(false);
            this.audioPlayer.play();
            this.sendProgressTracking();
            this.updateTrackingListeners();
        },
        switchTrack(resume) {
            const { currentTime } = this;
            const trackUrl = this.activeItem.post[`mp3_${this.drums ? 'yes' : 'no'}_drums_${this.metronome ? 'yes' : 'no'}_click_url`];
            this.updateSource(trackUrl, resume, currentTime);
            if (!resume) {
                this.$nextTick(() => {
                    const domElement = this.$refs[`list${this.activeItem.id}`][0].$el;
                    domElement.scrollIntoView({ block: "start" })
                });
            }
        },
        updateSource(source, resume, time) {
            this.audioPlayer.src = source;
            const resumeTrack = () => {
                this.seek(time);
                this.audioPlayer.removeEventListener('canplay', resumeTrack);
            };
            if (resume) {
                this.audioPlayer.addEventListener('canplay', resumeTrack);
            }
        },
        async playPreviousTrack() {
            if (this.loading) {
                return false;
            }
            let contentToPlay;
            const currentIndex = this.content.map(item => item.id).indexOf(this.activeItem.id);
            if (this.isShuffle) {
                const { index, page } = this.playedContent[1];
                if (page !== this.page) {
                    await this.handlePageChange({ page });
                }
                contentToPlay = this.content[index];
                this.$nextTick(() => {
                    this.updateTrack(contentToPlay);
                    this.shufflePlaylist.splice(0, 0, this.playedContent[0]);
                    this.playedContent.splice(0, 1);
                });
            } else {
                if (currentIndex === 0) {
                    const pageToGoTo = Number(this.page) === 1 ? this.totalPages : Number(this.page) - 1;
                    if (pageToGoTo !== this.page) {
                        await this.handlePageChange({ page: pageToGoTo });
                    }
                    // eslint-disable-next-line prefer-destructuring
                    contentToPlay = this.content[this.content.length - 1];
                } else {
                    contentToPlay = this.content[currentIndex - 1];
                }
                this.$nextTick(() => {
                    this.updateTrack(contentToPlay);
                });
            }
        },
        async playNextTrack() {
            if (this.loading) {
                return false;
            }
            let contentToPlay;
            if (this.isShuffle) {
                const { page, index } = this.shufflePlaylist[0];
                if (this.totalPages > 1 && page !== this.page) {
                    await this.handlePageChange({ page });
                }
                contentToPlay = this.content[index];
                this.playedContent.splice(0, 0, this.shufflePlaylist[0]);
                this.shufflePlaylist.splice(0, 1);
            } else {
                const currentIndex = this.content.map(content => content.id).indexOf(this.activeItem.id);
                if (currentIndex + 1 === this.content.length) {
                    const pageToGoTo = Number(this.page) === Number(this.totalPages)
                        ? 1
                        : Number(this.page) + 1;
                    if (pageToGoTo !== this.page) {
                        await this.handlePageChange({ page: pageToGoTo });
                    }
                    // eslint-disable-next-line prefer-destructuring
                    contentToPlay = this.content[0];
                } else {
                    contentToPlay = this.content[currentIndex + 1];
                }
            }
            this.$nextTick(() => {
                this.updateTrack(contentToPlay);
            });
        },
        seek(position) {
            this.audioPlayer.currentTime = position;
        },
        setRate(rate) {
            let _rate = rate;
            if (rate > 2) {
                _rate = 2;
            }
            if (rate < 0.25) {
                _rate = 0.25;
            }
            this.currentPlaybackRate = _rate;
            this.audioPlayer.playbackRate = _rate;
        },
        changeVolume(volume) {
            if (volume > 100 || volume < 0) {
                return false;
            }
            this.audioPlayer.volume = Math.round(volume) / 100;
            this.currentVolume = this.audioPlayer.volume;
            if (volume !== 0) {
                localStorage.setItem('playAlongsVolume', volume);
            }
        },
        toggleDrums() {
            this.drums = !this.drums;
            this.switchTrack(true);
            this.playPause();
        },
        toggleMetronome() {
            this.metronome = !this.metronome;
            this.switchTrack(true);
            this.playPause();
        },
        toggleLoop() {
            this.loop = !this.loop;
            this.audioPlayer.loop = this.loop;
            this.anchorOffsets.a = 0;
            this.anchorOffsets.b = 100;
        },
        handleAnchorMouseDown(anchor) {
            this.anchorMouseDown[anchor] = true;
        },
        handleAnchorButtonClick(anchor) {
            let offset = (this.currentTime / this.totalDuration) * 100;
            if (offset > 100) {
                offset = 100;
            } else if (offset < 0) {
                offset = 0;
            }
            this.anchorOffsets[anchor] = offset;
        },
        resetAnchors() {
            this.anchorOffsets.a = 0;
            this.anchorOffsets.b = 100;
        },
        trackMousePosition(event) {
            const posX = event.clientX != null ? event.clientX : event.touches[0].clientX;
            const posY = event.clientY != null ? event.clientY : event.touches[0].clientY;
            this.currentMousePosition.x = posX;
            this.currentMousePosition.y = posY;
            this.handleAnchorDrag();
        },
        handleAnchorDrag() {
            let mousePositionOffset = this.getDurationOffsetPercentageByMousePosition();
            if (this.anchorMouseDown.a) {
                if (mousePositionOffset >= this.anchorOffsets.b - 1) {
                    mousePositionOffset = this.anchorOffsets.b - 1;
                } else if (mousePositionOffset < 0) {
                    mousePositionOffset = 0;
                }
                this.anchorOffsets.a = mousePositionOffset;
            }
            if (this.anchorMouseDown.b) {
                if (mousePositionOffset <= this.anchorOffsets.a + 1) {
                    mousePositionOffset = this.anchorOffsets.a + 1;
                } else if (mousePositionOffset > 100) {
                    mousePositionOffset = 100;
                }
                this.anchorOffsets.b = mousePositionOffset;
            }
        },
        getDurationOffsetPercentageByMousePosition() {
            const sidebarOffset = this.isSidebarCollapsed ? 68 : 256;
            return ((this.currentMousePosition.x - sidebarOffset) / this.$refs.progressBar.$el.clientWidth) * 100;
        },
        mouseUpEventHandler() {
            this.anchorMouseDown.a = false;
            this.anchorMouseDown.b = false;
        },
        updatePageUrl() {
            const new_url = `${window.location.origin + window.location.pathname}?${this.filterQueryString}`;
            window.history.replaceState(window.history.state, null, new_url);
        },
        handleFilterChange(value) {
            this.page = 1;
            const itemIndex = this.selectedFilters.findIndex(f => f === value);

            if (itemIndex === -1) this.selectedFilters.push(value);
            else this.selectedFilters.splice(itemIndex, 1);

            this.updateContent(this.isShuffle)
        },
        handleClearFilter() {
            this.selectedFilters = [];
            this.progress = '';

            this.updateContent(this.isShuffle)
        },
        handleProgressChange(value) {
            this.progress = value;

            this.updateContent(this.isShuffle)
        },
        handlePageChange({ page }) {
            this.page = page;
            if (this.useUrlParams) {
                this.updatePageUrl();
            }
            return this.getContent();
        },
        handleTriggerSearch(value) {
            this.searchTerm = value;
            return this.getContent();
        },
        handleContentSort(value) {
            this.sort = value;
            if (this.useUrlParams) {
                this.updatePageUrl();
            }

            this.trackSort();
            return this.getContent();
        },
        handleContentLimit(event) {
            this.limit = event.target.value;
            this.page = 1;
            if (this.useUrlParams) {
                this.updatePageUrl();
            }
            this.fixPlaylistIndexes();
            this.fixPlayedContentIndexes();
            return this.getContent();
        },
        generateRandomPlaylist() {
            const indexes = Utils.range(this.totalResults);
            for (let i = indexes.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
            }
            this.shufflePlaylist = indexes.map(index => (this.getTrackByIndex(index)));
        },
        fixPlaylistIndexes() {
            this.shufflePlaylist = this.shufflePlaylist.map(item => (this.getTrackByIndex(item.originalIndex)));
        },
        fixPlayedContentIndexes() {
            this.playedContent = this.playedContent.map(item => (this.getTrackByIndex(item.originalIndex)));
        },
        getTrackByIndex(originalIndex) {
            const page = Math.ceil(originalIndex / this.limit) || 1;
            const index = originalIndex - ((page - 1) * this.limit);
            return { page, index, originalIndex };
        },
        toggleFavorites() {
            this.page = 1;
            this.showFavoritesOnly = !this.showFavoritesOnly;
            this.updatePageUrl();
            this.$nextTick(() => {
                this.playedContent = [];
                return this.getContent(true);
            });
        },
        toggleCompleted() {
            this.page = 1;
            this.showCompletedOnly = !this.showCompletedOnly;
            this.updatePageUrl();
            this.$nextTick(() => {
                this.playedContent = [];
                return this.getContent(true);
            });
        },
        getDefaultVolume() {
            if (window.localStorage.getItem('playAlongsVolume') != null) {
                this.changeVolume(Number(window.localStorage.getItem('playAlongsVolume')));
            }
        },
        enableKeyboardControls() {
            document.addEventListener('keydown', this.keyboardControlEventHandler);
            this.isKeyboardControlsEnabled = true;
            document.addEventListener('focusin', (event) => {
                const element = event.target;
                const isInputElement = element.matches('input[type="text"]')
                    || element.matches('input[type="password"]')
                    || element.matches('input[type="email"]')
                    || element.matches('textarea');
                if (isInputElement) {
                    document.removeEventListener('keydown', this.keyboardControlEventHandler);
                    this.isKeyboardControlsEnabled = false;
                }
            });
            document.addEventListener('focusout', () => {
                if (!this.isKeyboardControlsEnabled) {
                    document.addEventListener('keydown', this.keyboardControlEventHandler);
                    this.isKeyboardControlsEnabled = true;
                }
            });
        },
        keyboardControlEventHandler(event) {
            if (this.keyboardEventHandlers[event.code] && this.activeItem) {
                event.stopPropagation();
                event.preventDefault();
                this.keyboardEventHandlers[event.code]();
            }
        },
        toggleShuffle() {
            this.playedContent = [];
            this.isShuffle = !this.isShuffle;
            if (this.isShuffle) {
                this.generateRandomPlaylist();
                this.playNextTrack();
            } else {
                this.shufflePlaylist = [];
            }
        },
        addMouseEventHandlers() {
            document.addEventListener('mousemove', this.trackMousePosition);
            document.addEventListener('touchmove', this.trackMousePosition);
            document.addEventListener('mouseup', this.mouseUpEventHandler);
            document.addEventListener('touchend', this.mouseUpEventHandler);
        },
        removeMouseEventHandlers() {
            document.removeEventListener('mousemove', this.trackMousePosition);
            document.removeEventListener('touchmove', this.trackMousePosition);
            document.removeEventListener('mouseup', this.mouseUpEventHandler);
            document.removeEventListener('touchend', this.mouseUpEventHandler);
        },
        completedEventHandler(id) {
            const contentToComplete = this.content.find(item => item.id === id);
            if (contentToComplete.completed === true) {
                contentStatusReset(id);
            } else {
                contentStatusCompleted(id);
            }
            contentToComplete.completed = !contentToComplete.completed;
        },
        sendProgressTracking() {
            if (this.trackProgress) {
                this.progressTracker.send({
                    contentId: this.activeItem.id,
                    mediaType: 'practice',
                    mediaCategory: 'play-alongs',
                    watchPosition: this.currentTime,
                    totalDuration: this.totalDuration,
                    sessionToken: this.sessionToken,
                });
            }
        },

        updateTrackingListeners() {
            window.removeEventListener('visibilitychange', this.sendProgressTracking);
            this.$nextTick(() => {
                document.addEventListener('visibilitychange', () => {
                    this.sendProgressTracking();
                });
            });
            window.removeEventListener('pagehide', this.sendProgressTracking);
            this.$nextTick(() => {
                document.addEventListener('pagehide', () => {
                    this.sendProgressTracking();
                });
            });
        },
    },
};
</script>
